/**
 * web/src/api/gmailPoll.ts
 * Admin testing client for POST /api/admin/gmail-poll
 * (server: src/pixel/api/admin/gmail_poll.py).
 *
 * Runs the inbound Gmail reply-poll on demand so a tester can verify receipt
 * tracking against a workflow parked at welcome_receipt/ops_receipt without
 * waiting for the 5-minute cron.
 */
import { api } from '../lib/api';

/** Per-reviewer breakdown line in a poll summary. */
export interface GmailPollPerUser {
  user: string;
  fetched: number;
  outcomes?: Record<string, number>;
  /** Count of distinct tracked-recipient addresses this reviewer was scoped to. */
  candidates?: number;
  error?: boolean;
}

/** Mirror of the server's run_gmail_reply_poll summary dict. */
export interface GmailPollSummary {
  since: string;
  polledAt: string;
  users: number;
  fetched: number;
  processed: number;
  /** Tally keyed by InboundOutcome (no_match, classified_auto_confirmed, ...). */
  outcomes: Record<string, number>;
  cursorAdvanced: boolean;
  perUser: GmailPollPerUser[];
  inboundReplySource: string;
}

/**
 * Triggers the Gmail inbound-reply poll once and returns the summary.
 *
 * @param sinceMinutes optional override — re-scan replies from the last N
 *   minutes (does NOT move the cron cursor). Omit to mirror the cron exactly.
 * @returns the poll summary (fetched/processed counts + per-outcome tally)
 * @throws ApiClientError(403) when the caller isn't an admin
 * @sideEffects HTTP POST; may confirm tracked emails + resume workflows
 * @idempotent yes — re-processing is deduped on TrackedEmailInbound.gmailMessageId
 */
export async function triggerGmailPoll(sinceMinutes?: number): Promise<GmailPollSummary> {
  const qs = sinceMinutes ? `?since_minutes=${sinceMinutes}` : '';
  return api<GmailPollSummary>(`/api/admin/gmail-poll${qs}`, { method: 'POST' });
}
